import { useState } from "react";
import { Task } from "@/types/v2";
import { useTasks } from "@/hooks/store/useTasks";
import { useToast } from "@/hooks/useToast";

export interface TaskListActions {
  sessionCompletedIds: ReadonlySet<string>;
  editingTask: Task | undefined;
  setEditingTask: (task: Task | undefined) => void;
  taskToDelete: Task | undefined;
  setTaskToDelete: (task: Task | undefined) => void;
  rowHandlers: {
    onToggleComplete: (task: Task) => void;
    onEdit: (task: Task) => void;
    onDelete: (task: Task) => void;
  };
  confirmDelete: () => Promise<void>;
  resetSession: () => void;
}

/**
 * Shared task-list interaction wiring used by both the shop-wide Tasks tab and
 * the RO-scoped task strip: complete/uncomplete (with a session-completed set
 * that keeps just-completed rows visibly struck through), delete, edit, and the
 * associated toasts. Keeping this in one place stops the two surfaces diverging.
 */
export function useTaskListActions(): TaskListActions {
  const { updateTask, completeTask, deleteTask } = useTasks();
  const { toast } = useToast();

  const [editingTask, setEditingTask] = useState<Task | undefined>();
  const [taskToDelete, setTaskToDelete] = useState<Task | undefined>();
  const [sessionCompletedIds, setSessionCompletedIds] = useState<Set<string>>(
    () => new Set()
  );

  const handleToggleComplete = async (task: Task) => {
    // A task rendered in a list always came from Firestore, so id is set.
    const taskId = task.id as string;
    try {
      if (task.completed) {
        await updateTask(taskId, { completed: false });
        setSessionCompletedIds((prev) => {
          const next = new Set(prev);
          next.delete(taskId);
          return next;
        });
      } else {
        await completeTask(taskId);
        setSessionCompletedIds((prev) => new Set(prev).add(taskId));
      }
    } catch (error) {
      console.error("Failed to update task:", error);
      toast({ title: "Failed to update task", variant: "destructive" });
    }
  };

  const confirmDelete = async () => {
    if (!taskToDelete) return;
    try {
      await deleteTask(taskToDelete.id as string);
    } catch (error) {
      console.error("Failed to delete task:", error);
      toast({ title: "Failed to delete task", variant: "destructive" });
    } finally {
      setTaskToDelete(undefined);
    }
  };

  const resetSession = () => setSessionCompletedIds(new Set());

  return {
    sessionCompletedIds,
    editingTask,
    setEditingTask,
    taskToDelete,
    setTaskToDelete,
    rowHandlers: {
      onToggleComplete: handleToggleComplete,
      onEdit: setEditingTask,
      onDelete: setTaskToDelete,
    },
    confirmDelete,
    resetSession,
  };
}
